import React, { useState, useEffect, useCallback } from 'react';
import { Search } from 'lucide-react';
import { cn } from 'lib/utils';

export interface SearchInputProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    debounceMs?: number;
    className?: string;
    onEnter?: () => void;
    onKeyPress?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

const SearchInput: React.FC<SearchInputProps> = ({
    value,
    onChange,
    placeholder = '검색...',
    debounceMs = 300,
    className = '',
    onEnter,
    onKeyPress,
}) => {
    const [localValue, setLocalValue] = useState(value);

    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (localValue !== value) {
                onChange(localValue);
            }
        }, debounceMs);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localValue, debounceMs]);

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setLocalValue(e.target.value);
    }, []);

    const handleKeyPress = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter' && onEnter) {
                onEnter();
            }
            if (onKeyPress) {
                onKeyPress(e);
            }
        },
        [onEnter, onKeyPress]
    );

    return (
        <div className={cn('relative flex-1 min-w-[200px]', className)}>
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <input
                type="text"
                value={localValue}
                onChange={handleChange}
                placeholder={placeholder}
                onKeyPress={handleKeyPress}
                className={cn(
                    'h-9 w-full rounded border border-input bg-background pl-9 pr-3 text-base text-foreground transition-colors duration-fast',
                    'placeholder:text-neutral-400',
                    'focus:outline-none focus:border-ring focus:ring-1 focus:ring-ring'
                )}
            />
        </div>
    );
};

export default SearchInput;
